import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  UserCheck,
  UserX,
  Loader2,
  Clock,
  User,
  Mail,
  Phone,
  Briefcase,
  Building,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Eye
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

export default function AdminPendingApprovals() {
  const { authAxios } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const fetchPendingApprovals = useCallback(async () => {
    try {
      const res = await authAxios.get('/pending-approvals');
      setPendingUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  const handleApprove = async (user) => {
    setProcessing(user.id);
    try {
      const res = await authAxios.post(`/pending-approvals/${user.id}/action`, {
        action: 'approve'
      });
      toast.success(`${user.first_name} ${user.last_name} has been approved! Employee ID: ${res.data.employee_id}`);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve employee');
    } finally {
      setProcessing(null);
    }
  };

  const openRejectDialog = (user) => {
    setSelectedUser(user);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedUser) return;

    setProcessing(selectedUser.id);
    try {
      await authAxios.post(`/pending-approvals/${selectedUser.id}/action`, {
        action: 'reject',
        reason: rejectReason || null
      });
      toast.success(`${selectedUser.first_name} ${selectedUser.last_name}'s registration has been rejected`);
      setRejectDialogOpen(false);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject employee');
    } finally {
      setProcessing(null);
    }
  };

  const openDetailsDialog = (user) => {
    setSelectedUser(user);
    setDetailsDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return fmtTz(dateString, 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return fmtTz(dateString, 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-pending-approvals">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-stone-900 font-heading">Pending Approvals</h1>
        <p className="text-stone-500 mt-1">Review and approve new employee registrations</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : pendingUsers.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">All Caught Up!</h3>
            <p className="text-stone-500">No pending registrations to review</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Awaiting Review ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-100">
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Department</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Job Title</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Registered</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map((user) => (
                    <TableRow key={user.id} className="border-stone-100 hover:bg-stone-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-sm">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </div>
                          <div>
                            <p className="font-medium">{user.first_name} {user.last_name}</p>
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                              Pending
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-stone-600">{user.email}</TableCell>
                      <TableCell className="text-stone-600">{user.department}</TableCell>
                      <TableCell className="text-stone-600">{user.job_title}</TableCell>
                      <TableCell className="text-stone-600">{formatDateTime(user.registered_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetailsDialog(user)}
                            className="text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                            data-testid={`view-details-${user.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Details
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(user)}
                            disabled={processing === user.id}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid={`approve-${user.id}`}
                          >
                            {processing === user.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4 mr-1" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRejectDialog(user)}
                            disabled={processing === user.id}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`reject-${user.id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Registration Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-lg">
                  {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedUser.first_name} {selectedUser.last_name}</h3>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    Pending Approval
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <p className="text-stone-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </p>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-stone-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone
                  </p>
                  <p className="font-medium">{selectedUser.phone}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-stone-500 flex items-center gap-1">
                    <Building className="w-3 h-3" /> Department
                  </p>
                  <p className="font-medium">{selectedUser.department}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-stone-500 flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> Job Title
                  </p>
                  <p className="font-medium">{selectedUser.job_title}</p>
                </div>
                {selectedUser.date_of_birth && (
                  <div className="space-y-1">
                    <p className="text-stone-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Date of Birth
                    </p>
                    <p className="font-medium">{formatDate(selectedUser.date_of_birth)}</p>
                  </div>
                )}
                <div className="space-y-1">
                  <p className="text-stone-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Registered
                  </p>
                  <p className="font-medium">{formatDateTime(selectedUser.registered_at)}</p>
                </div>
              </div>

              {(selectedUser.emergency_contact_name || selectedUser.emergency_contact_phone) && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-stone-700 mb-2">Emergency Contact</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                      <p className="text-stone-500">Name</p>
                      <p className="font-medium">{selectedUser.emergency_contact_name || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-stone-500">Phone</p>
                      <p className="font-medium">{selectedUser.emergency_contact_phone || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDetailsDialogOpen(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setDetailsDialogOpen(false);
                handleApprove(selectedUser);
              }}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
              Reject Registration
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <p className="text-stone-600">
                Are you sure you want to reject the registration for{' '}
                <strong>{selectedUser.first_name} {selectedUser.last_name}</strong>?
              </p>
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (Optional)</Label>
                <Textarea
                  id="reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide a reason for rejection..."
                  className="bg-stone-50"
                  data-testid="reject-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={processing === selectedUser?.id}
              className="bg-rose-600 hover:bg-rose-500"
              data-testid="confirm-reject-btn"
            >
              {processing === selectedUser?.id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
